import React, { useRef } from "react";
import { Link } from "react-router-dom";

import {
  ArrowRight,
  Bell,
  BarChart3,
  BrainCircuit,
  Check,
  CheckCircle2,
  Gavel,
  MapPin,
  MessageSquare,
  Mic,
  MousePointer2,
  Network,
  RefreshCw,
  ShieldCheck,
  Smartphone,

  Trophy,
  Truck,
  Users,
  X,
} from "lucide-react";
import lightTrails from "@/assets/hero-light-trails.webp";

/**
 * Homepage hero — private to the homepage.
 * Dark, dense freight-tech composition: copy left (~35%), a layered
 * laptop + phone product mockup right (~65%) built from real HTML/CSS,
 * with floating data cards layered around it and a 4-item value strip
 * anchored at the bottom.
 */

const TEAL = "#0FA88E";

const bidRows = [
  {
    broker: "Cornerstone Freight Partners LLC",
    amount: "$6,900",
    rpm: "$4.42/mi",
    delta: "$100 above your budget",
    submitted: "Submitted 52m ago",
    chips: ["Team Available", "ETA 1h"],
    tag: "Lowest Bid",
    note: "“Empty in Dallas now, true team for monday am”",
    highlight: true,
  },
  {
    broker: "Ironwood Transport Brokers LLC",
    amount: "$6,975",
    rpm: "$4.47/mi",
    delta: "$175 above your budget",
    submitted: "Submitted 53m ago",
    chips: ["Ready to Cover", "Truck in Area", "ETA 7h"],
    note: "“I can check in at 3pm if I have a strong solo, monday pm delivery if that works”",
  },
  {
    broker: "Meridian Freight Solutions LLC",
    amount: "$7,150",
    rpm: "$4.58/mi",
    delta: "$350 above your budget",
    submitted: "Submitted 55m ago",
    chips: ["Ready to Cover", "Team Available", "ETA 7h"],
    note: "“Can deliver sunday night”",
  },
  {
    broker: "Cascade Logistics Group, Inc.",
    amount: "$7,325",
    rpm: "$4.69/mi",
    delta: "$525 above your budget",
    submitted: "Submitted 54m ago",
    chips: ["Ready to Cover", "Truck in Area", "ETA 1h"],
    note: "“Monday morning, solid team ready to go!”",
  },
];

const Chip: React.FC<{ children: React.ReactNode; tone?: "muted" | "teal" | "amber" }> = ({
  children,
  tone = "muted",
}) => {
  const tones = {
    muted: "bg-slate-100 text-slate-600 border-slate-200",
    teal: "bg-[#0FA88E]/10 text-[#0B7D69] border-[#0FA88E]/30",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  } as const;
  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-[1px] text-[7.5px] font-semibold leading-[1.4] ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

const HomeHero: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  const scrollToNext = () => {
    const next = sectionRef.current?.nextElementSibling;
    if (next) next.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  return (
    <section ref={sectionRef} className="relative overflow-hidden bg-[#06131A]">
      {/* ── LAYER 1: base — near-black with cool teal cast ───── */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(165deg,#06131A 0%, #071820 55%, #081D26 100%)",
        }}
      />

      {/* ── LAYER 2: decorative background (edge-weighted, faint) ── */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {/* soft ambient glows */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 55% at 92% 8%, rgba(0,207,196,0.16) 0%, rgba(6,19,26,0) 70%), radial-gradient(ellipse 55% 50% at 4% 92%, rgba(23,229,221,0.10) 0%, rgba(6,19,26,0) 72%), radial-gradient(ellipse 40% 40% at 50% 115%, rgba(15,168,142,0.10) 0%, rgba(6,19,26,0) 70%)",
          }}
        />
        {/* glowing light-trails artwork — soft-alpha PNG, screen-blended */}
        <img
          src={lightTrails}
          alt=""
          aria-hidden
          loading="eager"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-[0.32] mix-blend-screen md:opacity-[0.4]"
          style={{
            maskImage:
              "radial-gradient(ellipse 68% 70% at 50% 45%, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.35) 55%, #000 95%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 68% 70% at 50% 45%, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.35) 55%, #000 95%)",
          }}
        />
      </div>


      {/* ── LAYER 2b: bottom edge glow ─────────────────────── */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 z-[1]">
        <div
          className="mx-auto h-16 w-[86%] max-w-[1500px]"
          style={{
            background:
              "radial-gradient(ellipse 70% 100% at 50% 100%, rgba(0,207,196,0.14) 0%, rgba(6,19,26,0) 75%)",
          }}
        />
        <div
          className="mx-auto h-px w-[86%] max-w-[1500px]"
          style={{
            background:
              "linear-gradient(90deg, rgba(0,207,196,0) 0%, rgba(23,229,221,0.4) 22%, rgba(23,229,221,0.52) 50%, rgba(23,229,221,0.4) 78%, rgba(0,207,196,0) 100%)",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 pt-20 pb-0 lg:px-10 lg:pt-28">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-6">
          {/* ── Copy column (~35%) ───────────────────────────── */}
          <div className="lg:col-span-4">
            <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#3ED9BE]">
              FREIGHT SOURCING &amp; SHIPMENT MANAGEMENT
            </p>

            <h1 className="mt-5 text-[40px] font-bold leading-[1.06] tracking-[-0.025em] text-white sm:text-[50px] lg:text-[54px] xl:text-[58px]">
              One Platform.
              <br />
              Total Freight Clarity.
            </h1>

            <p className="mt-6 max-w-[460px] text-[15.5px] leading-[1.65] text-[#A9BFBC]">
              The all-in-one platform that helps shippers source capacity faster, compare every quote
              in one place, and manage each shipment from award to delivery.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-5">
              <Link
                to="/auth?mode=signup&role=shipper"
                className="inline-flex items-center justify-center rounded-lg bg-[#0C7A68] px-7 py-[15px] text-[15px] font-bold text-white shadow-[0_12px_34px_-10px_rgba(15,168,142,0.8)] transition-all duration-200 hover:bg-[#0FA88E] hover:-translate-y-[1px]"
              >
                Get Started Free
              </Link>
              <button
                type="button"
                onClick={() => document.getElementById("see-it-in-action")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="group inline-flex items-center gap-2 text-[15px] font-semibold text-white transition-colors hover:text-[#3ED9BE]"
              >
                Watch How It Works
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>

            {/* ── Get the App card (mockup clone) ─────────────── */}
            <div className="mt-7 max-w-[430px] rounded-2xl border border-[#1E3A38] bg-[#0A1A1E]/80 p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <span aria-hidden className="relative flex h-12 w-12 shrink-0 items-center justify-center">
                  {/* mockup glow / halo */}
                  <span className="absolute inset-0 rounded-full bg-[#3ED9BE]/30 blur-xl" />
                  <span className="absolute -inset-2 rounded-full bg-[#3ED9BE]/15 blur-2xl" />
                  <span className="relative flex h-12 w-12 items-center justify-center rounded-xl border border-[#3ED9BE]/40 bg-[#0E2A2A] shadow-[0_0_18px_rgba(62,217,190,0.35)]">
                    <Smartphone className="h-6 w-6 text-[#3ED9BE]" strokeWidth={1.75} />
                  </span>
                </span>
                <div className="min-w-0">
                  <h2 className="text-[19px] font-bold leading-tight text-white">Get the App</h2>
                  <p className="mt-1.5 text-[13.5px] leading-[1.6] text-[#A9BFBC]">
                    Manage shipments, get real-time updates, and stay in control—anytime, anywhere.
                  </p>
                </div>
              </div>

              <p className="mt-4 text-[12.5px] text-[#7C918F]">Available on iPhone and Android.</p>

              <div className="mt-3 flex flex-wrap items-center gap-3">
                <a
                  href="https://play.google.com/store/apps/details?id=com.lanerunner.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get LaneRunner on Google Play"
                  className="inline-flex min-h-[56px] flex-1 basis-[170px] items-center justify-center gap-2.5 rounded-lg border border-[#2A3A3A] bg-black px-4 py-2.5 transition-colors hover:border-[#3ED9BE]/50"
                >
                  <svg viewBox="0 0 512 512" aria-hidden className="h-7 w-7 shrink-0">
                    <path fill="#00D0FF" d="M47 20c-6 4-10 12-10 22v428c0 10 4 18 10 22l232-236z" />
                    <path fill="#FFCE00" d="M357 176 279 234l78 78 84-48c14-8 14-32 0-40z" />
                    <path fill="#FF3A44" d="M357 176 111 27c-13-8-25-9-33-4l201 211z" />
                    <path fill="#00E676" d="M279 234 78 489c8 5 20 4 33-4l246-149z" />
                  </svg>
                  <span className="leading-tight text-white">
                    <span className="block text-[9.5px] font-medium uppercase tracking-[0.14em] text-white">
                      Get it on
                    </span>
                    <span className="block text-[17px] font-semibold leading-tight">Google Play</span>
                  </span>
                </a>
                <a
                  href="https://apps.apple.com/us/app/lanerunner-freight-platform/id6805569150"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download LaneRunner on the App Store"
                  className="inline-flex min-h-[56px] flex-1 basis-[170px] items-center justify-center gap-2.5 rounded-lg border border-[#2A3A3A] bg-black px-4 py-2.5 transition-colors hover:border-[#3ED9BE]/50"
                >
                  <svg viewBox="0 0 384 512" aria-hidden className="h-7 w-7 shrink-0 fill-white">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-36.8-2.8-77 21.3-91.7 21.3-15.5 0-51.1-20.3-79-20.3C61.2 141.6 8 184.5 8 271.3c0 25.6 4.7 52 14.1 79.3 12.6 35.9 56.4 124 102.1 122.6 23.9-.6 40.8-17 71.9-17 30.2 0 45.8 17 72.5 17 46.1-.7 85.7-80.8 97.7-116.8-61.8-29.1-47.6-85.9-47.6-87.7zM255.9 84.6c17.7-21 27-45.4 25.4-73-25.7 1.5-49.7 13.8-67.9 34.2-17.9 19.9-26.4 43.8-24.5 70.1 27-1.3 51.2-13.5 67-31.3z" />
                  </svg>
                  <span className="leading-tight text-white">
                    <span className="block text-[9.5px] font-medium tracking-[0.02em] text-white">
                      Download on the
                    </span>
                    <span className="block text-[17px] font-semibold leading-tight">App Store</span>
                  </span>
                </a>
              </div>
            </div>


          </div>

          {/* ── Product visual column (~65%) ─────────────────── */}
          <div className="lg:col-span-8">
            <div className="h-[290px] overflow-hidden sm:h-[450px] lg:h-auto lg:overflow-visible">
            <div className="relative mx-auto w-[900px] origin-top-left scale-[0.4] sm:scale-[0.62] lg:w-full lg:max-w-none lg:scale-100">

              {/* glow behind the device */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-8 top-16 bottom-10 rounded-[60px] blur-[70px]"
                style={{
                  background:
                    "radial-gradient(ellipse at 50% 50%, rgba(15,168,142,0.35), rgba(15,168,142,0) 70%)",
                }}
              />

              {/* ── top floating metrics strip ─────────────── */}
              <div className="relative z-20 mx-auto mb-3 flex w-fit items-stretch gap-2 rounded-xl border border-white/10 bg-[#0B1517]/95 p-2 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)] backdrop-blur">
                <div className="flex items-center gap-2 rounded-lg px-3 py-1.5">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.06]">
                    <MessageSquare className="h-3.5 w-3.5 text-[#3ED9BE]" />
                  </span>
                  <div>
                    <div className="text-[15px] font-bold leading-none text-white">12</div>
                    <div className="mt-0.5 text-[9.5px] text-white/50">Quotes Received</div>
                  </div>
                </div>
                {[
                  ["See the market clearly.", "$2,850", "text-white"],
                  ["Instant Capacity", "$3,075", "text-white"],
                  ["Tap into more options fast.", "$3,340", "text-[#FF7A6B]"],
                ].map(([l, v, c]) => (
                  <div
                    key={l}
                    className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-1.5"
                  >
                    <div className="text-[8.5px] font-bold uppercase tracking-[0.12em] text-white/45">
                      {l}
                    </div>
                    <div className={`mt-0.5 text-[15px] font-bold ${c}`}>{v}</div>
                  </div>
                ))}
              </div>

              {/* ── laptop ─────────────────────────────────── */}
              <div className="relative z-10">
                {/* lid */}
                <div className="rounded-t-[14px] border border-white/12 bg-gradient-to-b from-[#2A3436] to-[#151E20] p-[8px] shadow-[0_50px_120px_-30px_rgba(0,0,0,0.95)]">
                  {/* screen */}
                  <div className="overflow-hidden rounded-[8px] bg-[#F4F7F8]">
                    {/* app top bar */}
                    <div className="flex items-center justify-between border-b border-slate-200 bg-white px-3 py-2">
                      <div className="flex items-center gap-2">
                        <ArrowRight className="h-3 w-3 rotate-180 text-slate-400" />
                        <span className="h-2 w-2 rounded-full bg-red-500" />
                        <span className="text-[10px] font-bold text-slate-900">Bidding Live</span>
                        <span className="text-[9px] font-medium text-slate-400">LR-C4F822</span>
                      </div>
                      <div className="hidden items-center gap-3 text-[8.5px] text-slate-500 sm:flex">
                        <span>Closes in 3h 42m</span>
                        <span>· 4 bidding</span>
                        <span>· 1 slot left</span>
                      </div>
                    </div>

                    {/* 3-column workspace */}
                    <div className="grid grid-cols-12 gap-2 p-2">
                      {/* left rail — load summary */}
                      <div className="col-span-3 space-y-2">
                        <div className="rounded-lg border border-slate-200 bg-white p-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[8px] font-bold text-slate-700">LR-C4F822</span>
                            <div className="flex gap-1">
                              <Chip>FTL</Chip>
                              <Chip>FcFS</Chip>
                            </div>
                          </div>
                          <div className="mt-2 text-[7.5px] font-bold uppercase tracking-wider text-slate-400">
                            Lane
                          </div>
                          <div className="mt-1 space-y-1">
                            <div className="flex items-center gap-1.5 text-[9px] font-semibold text-slate-800">
                              <span className="h-1.5 w-1.5 rounded-full bg-[#0FA88E]" />
                              Dallas, TX
                            </div>
                            <div className="flex items-center gap-1.5 text-[9px] font-semibold text-slate-800">
                              <span className="h-1.5 w-1.5 rounded-full bg-[#FF7A6B]" />
                              Bronx, NY
                            </div>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-1 border-t border-slate-100 pt-2">
                            <div>
                              <div className="text-[7px] font-bold uppercase tracking-wider text-slate-400">
                                Pickup
                              </div>
                              <div className="text-[8.5px] font-semibold text-slate-800">Jul 18</div>
                            </div>
                            <div>
                              <div className="text-[7px] font-bold uppercase tracking-wider text-slate-400">
                                Delivery
                              </div>
                              <div className="text-[8.5px] font-semibold text-slate-800">Jul 20</div>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-1">
                            <Chip tone="teal">1,561 mi</Chip>
                          </div>
                          <div className="mt-1.5">
                            <span className="inline-flex rounded bg-[#E11D48] px-1.5 py-[1px] text-[7.5px] font-bold text-white">
                              Today
                            </span>
                          </div>
                          <div className="mt-2 border-t border-slate-100 pt-2">
                            <div className="text-[7px] font-bold uppercase tracking-wider text-slate-400">
                              Equipment
                            </div>
                            <div className="mt-1 flex items-center gap-1.5 text-[9px] font-semibold text-slate-800">
                              <Truck className="h-3 w-3 text-slate-400" />
                              Reefer
                            </div>
                            <div className="text-[8px] text-slate-500">41,000 lbs · 53 ft</div>
                          </div>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-white p-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[8px] font-bold text-slate-700">LanePredictor</span>
                            <Chip>FEASIBILITY</Chip>
                          </div>
                          <div className="mt-1.5">
                            <span className="inline-flex rounded bg-[#0FA88E]/12 px-1.5 py-[1px] text-[8px] font-bold text-[#0B7D69]">
                              On Time
                            </span>
                          </div>
                          <div className="mt-1.5 text-[7.5px] leading-[1.5] text-slate-500">
                            Confidence 65% (capped — assumed times)
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-[7.5px] font-semibold text-slate-600">
                            <span>27.4 MPH required</span>
                            <span className="text-[#0B7D69]">+4.5h buffer</span>
                          </div>
                          <div className="mt-1.5 text-[7.5px] leading-[1.5] text-slate-500">
                            Confirm the driver has departed the shipper in Dallas and monitor for
                            consistent progress through the first shift.
                          </div>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-white p-2">
                          <div className="text-[7.5px] font-bold uppercase tracking-wider text-slate-400">
                            Instructions
                          </div>
                          <div className="mt-1 text-[8px] font-semibold leading-[1.5] text-slate-700">
                            Team Required | Pick Hrs: Noon | Drop Hrs: FCFS
                          </div>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-white p-2">
                          <div className="text-[7.5px] font-bold uppercase tracking-wider text-slate-400">
                            Your Budget
                          </div>
                          <div className="text-[13px] font-bold text-slate-900">Up to $6,800</div>
                          <div className="text-[7.5px] text-slate-500">$4.36/mi budget</div>
                        </div>
                      </div>

                      {/* center — live bid feed */}
                      <div className="col-span-6 rounded-lg border border-slate-200 bg-white p-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[9px] font-bold text-slate-900">Live Bid Feed</span>
                          <Chip tone="teal">Dropping</Chip>
                        </div>
                        <div className="mt-0.5 text-[7.5px] text-slate-500">
                          Low $6,900 · Avg $7,088 · High $7,325
                        </div>

                        <div className="mt-2 space-y-1.5">
                          {bidRows.map((b) => (
                            <div
                              key={b.broker}
                              className={`rounded-lg border p-2 ${
                                b.highlight
                                  ? "border-[#0FA88E]/40 bg-[#0FA88E]/[0.04]"
                                  : "border-slate-200 bg-white"
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <div className="w-[62px] shrink-0">
                                  <div className="text-[13px] font-bold leading-none text-slate-900">
                                    {b.amount}
                                  </div>
                                  <div className="mt-1 text-[7.5px] text-slate-500">{b.rpm}</div>
                                  <div className="mt-1 text-[7px] leading-[1.4] text-slate-400">
                                    {b.delta}
                                  </div>
                                </div>
                                <div className="min-w-0 flex-1 border-l border-slate-100 pl-2">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex min-w-0 items-center gap-1.5">
                                      <span className="h-4 w-4 shrink-0 rounded-full bg-slate-200" />
                                      <span className="truncate text-[8.5px] font-bold text-slate-900">
                                        {b.broker}
                                      </span>
                                      <CheckCircle2 className="h-2.5 w-2.5 shrink-0 text-[#0FA88E]" />
                                    </div>
                                    {b.tag && <Chip tone="teal">{b.tag}</Chip>}
                                  </div>
                                  <div className="mt-0.5 text-[7px] text-slate-400">
                                    {b.submitted}
                                  </div>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {b.chips.map((c) => (
                                      <Chip key={c}>{c}</Chip>
                                    ))}
                                  </div>
                                  <div className="mt-1 text-[7.5px] italic leading-[1.4] text-slate-500">
                                    {b.note}
                                  </div>
                                  <button className="mt-1.5 inline-flex items-center gap-1 rounded border border-[#0FA88E]/40 bg-white px-2 py-[3px] text-[7.5px] font-bold text-[#0B7D69]">
                                    <Trophy className="h-2.5 w-2.5" />
                                    Award this bid
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="mt-2 flex items-center justify-between text-[7.5px] text-slate-500">
                          <span>4 / 5 slots filled</span>
                          <span>1 remaining</span>
                        </div>
                        <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full w-4/5 rounded-full bg-[#0FA88E]" />
                        </div>
                      </div>

                      {/* right rail — award decision + atlas */}
                      <div className="col-span-3 space-y-2">
                        <div className="rounded-lg border border-slate-200 bg-white p-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[9px] font-bold text-slate-900">
                              Award Decision
                            </span>
                            <Chip tone="teal">Good Match</Chip>
                          </div>
                          <div className="mt-1.5 text-[7px] font-bold uppercase tracking-wider text-slate-400">
                            Recommended Winner
                          </div>
                          <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 p-2">
                            <div className="flex items-center justify-between">
                              <Chip tone="teal">Lowest Bid</Chip>
                              <span className="text-[7px] text-slate-400">52m ago</span>
                            </div>
                            <div className="mt-1 text-[14px] font-bold leading-none text-slate-900">
                              $6,900
                            </div>
                            <div className="text-[7.5px] text-slate-500">$4.42/mi</div>
                            <div className="mt-1 text-[8px] font-bold leading-[1.4] text-slate-800">
                              Cornerstone Freight Partners LLC
                            </div>
                            <div className="mt-1 flex gap-1">
                              <Chip>Team Available</Chip>
                              <Chip>ETA 1h</Chip>
                            </div>
                          </div>
                          <button className="mt-2 flex w-full items-center justify-center gap-1 rounded bg-[#0C7A68] py-[5px] text-[8px] font-bold text-white">
                            <Trophy className="h-2.5 w-2.5" />
                            AWARD LOAD
                          </button>
                          <div className="mt-1 text-center text-[6.5px] leading-[1.4] text-slate-400">
                            Awarding reveals your contact info to the winning broker only
                          </div>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-white p-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-[8.5px] font-bold text-slate-900">
                                Atlas Award Analysis
                              </div>
                              <div className="text-[7px] text-slate-400">Powered by Atlas AI</div>
                            </div>
                            <RefreshCw className="h-2.5 w-2.5 text-[#0FA88E]" />
                          </div>
                          <div className="mt-1.5 rounded border border-amber-200 bg-amber-50 p-1.5 text-[7px] leading-[1.5] text-amber-800">
                            Cornerstone is the cheaper team option near pickup; Meridian and Cascade
                            say they can cover now but cost more.
                          </div>
                          <div className="mt-1.5 flex items-center justify-between border-t border-slate-100 pt-1.5">
                            <span className="truncate text-[7.5px] font-semibold text-slate-700">
                              Cornerstone Freight Partners LLC
                            </span>
                            <span className="text-[7.5px] font-bold text-slate-900">$6,900</span>
                          </div>
                          <div className="mt-1 text-[7px] leading-[1.5] text-slate-500">
                            Team driver — matches your team requirement, can reach pickup in ~1h, but
                            is $100 over budget.
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            <Chip tone="amber">Over Budget</Chip>
                            <Chip tone="teal">Meets Team Requirement</Chip>
                            <Chip tone="teal">Fastest ETA (claim)</Chip>
                            <Chip tone="teal">Meets Pickup Window</Chip>
                            <Chip tone="teal">Verified Authority</Chip>
                            <Chip tone="teal">Credit ≥ 700</Chip>
                            <Chip tone="teal">Fast Response</Chip>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* base */}
                <div className="relative mx-auto h-[14px] w-[112%] -translate-x-[5.35%] rounded-b-[14px] bg-gradient-to-b from-[#2A3436] to-[#0D1416] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.9)]">
                  <div className="absolute left-1/2 top-0 h-[4px] w-[18%] -translate-x-1/2 rounded-b-full bg-[#0B1113]" />
                </div>
              </div>

              {/* ── phone overlapping bottom-left ───────────── */}
              <div className="absolute -bottom-6 left-0 z-20 hidden w-[150px] rounded-[18px] border border-white/15 bg-[#1B2426] p-[4px] shadow-[0_30px_70px_-16px_rgba(0,0,0,0.9)] lg:block xl:w-[172px]">
                <div className="overflow-hidden rounded-[14px] bg-[#F4F7F8]">
                  <div className="flex items-center justify-between px-2 py-1 text-[6px] text-slate-500">
                    <span>10:41</span>
                    <span>••• ▮</span>
                  </div>
                  <div className="px-2 pb-2">
                    <div className="flex items-center gap-1 text-[7px] font-bold text-slate-800">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#0FA88E]" />
                      LR-C4F822
                    </div>
                    <div className="mt-1 text-[9px] font-bold text-slate-900">
                      Dallas, TX → Bronx, NY
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                      <span className="rounded bg-[#E11D48] px-1 py-[1px] text-[6px] font-bold text-white">
                        Today
                      </span>
                      <span className="text-[6px] text-slate-500">Closing</span>
                    </div>

                    <div className="mt-1.5 rounded-md border border-slate-200 bg-white p-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[6.5px] font-bold text-slate-700">Trip Overview</span>
                        <span className="text-[6px] font-semibold text-[#0B7D69]">VIEW ROUTE</span>
                      </div>
                      <div className="mt-1 space-y-1">
                        <div className="flex items-center gap-1 text-[6.5px] font-semibold text-slate-800">
                          <MapPin className="h-2 w-2 text-[#0FA88E]" />
                          Dallas, TX (Origin) · Jul 18
                        </div>
                        <div className="flex items-center gap-1 text-[6.5px] font-semibold text-slate-800">
                          <MapPin className="h-2 w-2 text-[#FF7A6B]" />
                          Bronx, NY (Destination) · Jul 20
                        </div>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-[6px] text-slate-500">
                        <span>Reefer · 41,000 lbs</span>
                        <span>1,561 mi</span>
                      </div>
                    </div>

                    <div className="mt-1.5 rounded-md border border-[#0FA88E]/40 bg-white p-1.5">
                      <span className="rounded bg-[#0FA88E]/12 px-1 py-[1px] text-[6px] font-bold text-[#0B7D69]">
                        Lowest Bid
                      </span>
                      <div className="mt-1 text-[11px] font-bold leading-none text-slate-900">
                        $6,900
                      </div>
                      <div className="text-[6px] text-slate-500">$4.42/mi</div>
                      <div className="mt-1 text-[6.5px] font-bold leading-tight text-slate-800">
                        Cornerstone Freight Partners LLC
                      </div>
                      <div className="mt-1 flex gap-1">
                        <Chip>Team Available</Chip>
                        <Chip>ETA 1h</Chip>
                      </div>
                    </div>

                    <button className="mt-1.5 flex w-full items-center justify-center gap-1 rounded bg-[#0C7A68] py-[4px] text-[7px] font-bold text-white">
                      <Trophy className="h-2 w-2" />
                      Award Load
                    </button>

                    <div className="mt-1.5 flex justify-between border-t border-slate-200 pt-1 text-[5.5px] text-slate-400">
                      <span>Loads</span>
                      <span>Offers</span>
                      <span>Messages</span>
                      <span>Account</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── floating card: new bid toast (top-right) ── */}
              <div className="absolute -top-4 right-0 z-30 hidden w-[210px] rounded-xl border border-white/12 bg-white px-3 py-2.5 shadow-[0_26px_60px_-18px_rgba(0,0,0,0.9)] xl:block xl:-right-10">
                <div className="flex items-start gap-2.5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0C7A68]">
                    <Bell className="h-4 w-4 text-white" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-bold text-slate-900">New bid received</div>
                    <div className="text-[15px] font-bold leading-tight text-[#0B7D69]">$6,900</div>
                    <div className="text-[8.5px] font-semibold leading-tight text-slate-600">
                      Cornerstone Freight Partners LLC
                    </div>
                    <div className="text-[8px] text-slate-400">52m ago</div>
                  </div>
                  <X className="h-3 w-3 shrink-0 text-slate-300" />
                </div>
              </div>

              {/* ── floating card: verified broker (right) ──── */}
              <div className="absolute right-0 top-[26%] z-30 hidden w-[190px] rounded-xl border border-white/12 bg-white p-3 shadow-[0_26px_60px_-18px_rgba(0,0,0,0.9)] xl:block xl:-right-14">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-[#0B7D69]" />
                  <div>
                    <div className="text-[9.5px] font-bold text-slate-900">Verified Broker</div>
                    <div className="text-[9.5px] font-bold leading-tight text-slate-700">
                      Cornerstone Freight Partners LLC
                    </div>
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  {["Active Authority", "Insurance on File"].map((t) => (
                    <div key={t} className="flex items-center gap-1.5 text-[8.5px] text-slate-600">
                      <Check className="h-2.5 w-2.5 text-[#0FA88E]" />
                      {t}
                    </div>
                  ))}
                </div>
              </div>

              {/* ── floating card: on-time delivery (right-low) ── */}
              <div className="absolute right-0 bottom-[8%] z-30 hidden w-[200px] rounded-xl border border-white/12 bg-white p-3 shadow-[0_26px_60px_-18px_rgba(0,0,0,0.9)] xl:block xl:-right-8">
                <div className="text-[8px] font-bold uppercase tracking-[0.12em] text-slate-500">
                  On-Time Delivery
                </div>
                <div className="mt-1 flex items-end justify-between">
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-[24px] font-bold leading-none text-slate-900">96</span>
                    <span className="text-[13px] font-bold text-[#0B7D69]">%</span>
                  </div>
                  <div className="text-right text-[7.5px] leading-tight text-slate-500">
                    <div className="font-bold text-slate-700">Track Record</div>
                    245 Loads
                  </div>
                </div>
                <div className="mt-2 flex h-[54px] items-end gap-1.5 border-l border-b border-slate-200 pl-2 pb-1">
                  {[70, 82, 78, 88, 84, 92].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-t bg-[#0C7A68]"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
                <div className="mt-1 flex justify-between pl-2 text-[6px] text-slate-400">
                  {["JAN", "FEB", "MAR", "APR", "MAY", "JUN"].map((m) => (
                    <span key={m}>{m}</span>
                  ))}
                </div>
              </div>

              {/* ── floating card: team availability (left-low) ── */}
              <div className="absolute -bottom-10 left-[16%] z-30 hidden w-[220px] rounded-xl border border-white/12 bg-white p-3 shadow-[0_26px_60px_-18px_rgba(0,0,0,0.9)] lg:block">
                <div className="flex items-center gap-2">
                  <Users className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-[8.5px] font-bold uppercase tracking-[0.12em] text-slate-500">
                    Team Availability
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 border-t border-slate-100 pt-2">
                  <div>
                    <div className="text-[8.5px] text-slate-500">True Team</div>
                    <div className="text-[10px] font-bold text-slate-900">Ready Now</div>
                  </div>
                  <div>
                    <div className="text-[8.5px] text-slate-500">ETA to Pickup</div>
                    <div className="text-[10px] font-bold text-slate-900">1h 15m</div>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-[8.5px] font-semibold text-[#0B7D69]">
                  <CheckCircle2 className="h-3 w-3" />
                  Team Driver
                </div>
              </div>
            </div>
            </div>

          </div>
        </div>

        {/* ── bottom Atlas AI workflow strip ──────────────────────── */}
        <div className="mt-10 border-t border-white/[0.08] py-8 lg:mt-16">
          <p className="mb-6 text-center text-[14px] font-bold uppercase tracking-[0.14em] text-[#3ED9BE]">
            From Voice to Bookable Capacity — Powered by Atlas AI
          </p>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5 lg:divide-x lg:divide-white/[0.08]">
            {[
              [Mic, "Voice Post", "Speak the freight. Atlas builds the load."],
              [Network, "Instant Sourcing", "Your capacity network sees it instantly."],
              [Gavel, "Live Bidding", "Competing rates come to you."],
              [BrainCircuit, "Atlas Analysis", "AI compares rate, fit, history & risk."],
              [CheckCircle2, "Award in a Click", "Choose the best option and move."],
            ].map(([Icon, title, desc], i) => {
              const I = Icon as React.ElementType;
              const isAtlas = i === 3;
              return (
                <div
                  key={title as string}
                  className={`flex items-center gap-3 ${i > 0 ? "lg:pl-6" : ""} ${i === 0 ? "lg:pl-0" : ""}`}
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                      isAtlas
                        ? "border border-[#3ED9BE]/70 bg-[#0FA88E]/15 shadow-[0_0_14px_-2px_rgba(62,217,190,0.45)]"
                        : "border border-[#0FA88E]/30 bg-[#0FA88E]/10"
                    }`}
                  >
                    <I className={`text-[#3ED9BE] ${isAtlas ? "h-[18px] w-[18px]" : "h-4 w-4"}`} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-bold leading-tight text-white">
                      {title as string}
                    </div>
                    <div className="text-[13px] leading-snug text-[#8FA5A2]">
                      {desc as string}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
